<template>
    <div v-if="post" class="kc-card" @click="openLinkPost()">
        <!-- Image -->
        <div class="kc-card-img">
            <img
                v-if="pictureMedias"
                :src="`${baseUrl}/storage/${pictureMedias.id}?token=${token}`"
                :alt="post.title"
            />
            <img v-else src="/img/img20.jpg" alt="placeholder" />
        </div>

        <!-- Body -->
        <div class="kc-card-body">
            <!-- Ref + Title -->
            <div class="kc-card-header">
                <span class="kc-card-ref">#{{ post.reference }}</span>
                <span class="kc-card-title">{{ post.title | truncate(60) }}</span>
            </div>

            <!-- Status -->
            <div v-if="columnStatus" class="kc-card-status">
                <i v-if="(post.status && post.status.icon) || columnStatus.icon" :class="post.status ? post.status.icon : columnStatus.icon"></i>
                <span>{{ post.status ? post.status.name : columnStatus.title }}</span>
            </div>

            <!-- Form answers -->
            <ul v-if="visibleAnswers.length" class="kc-card-answers">
                <li v-for="answer in visibleAnswers" :key="answer.id" class="kc-card-answer">
                    <span class="kc-answer-label">{{ answer.label }}</span>
                    <span class="kc-answer-value">{{ answer.value }}</span>
                </li>
            </ul>

            <!-- Footer: date, priority, author -->
            <div class="kc-card-footer">
                <span v-if="post.created_at" class="kc-card-date">
                    <i class="fal fa-clock"></i>
                    {{ moment(post.created_at).format('L') }}
                </span>
                <div class="kc-card-meta">
                    <span v-if="post.priority" class="kc-card-priority" :class="'kc-priority--' + post.priority" :title="$t(`post.page.priority.${post.priority}`)">
                        <i :class="priorityIcon"></i>
                    </span>
                    <span v-if="post.created_by && post.created_by.name" class="kc-card-author" :title="post.created_by.name">
                        {{ getInitials(post.created_by.name) }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapState } from 'vuex'

export default {
    name: 'PanelKanban',
    props: {
        post: {
            required: true,
            type: Object
        },
        columnStatus: {
            required: false,
            type: Object,
            default: null
        }
    },
    data() {
        return {
            baseUrl: this.$store.state.baseURL
        }
    },
    computed: {
        ...mapState('auth', {
            token: (state) => state.token
        }),
        pictureMedias() {
            if (this.post && this.post.medias && this.post.medias.length > 0) {
                return this.post.medias.find((media) =>
                    /\.(png|jpg|jpeg|gif|webp)$/i.test(media.name)
                )
            }
            return null
        },
        visibleAnswers() {
            const SKIP = ['gdpr', 'file-upload', 'location', 'geolocation-automatic', 'category']
            const answers = (this.post && this.post.answers) || []
            return answers
                .filter((a) => !SKIP.includes(a.name) && a.hidden !== 1 && a.value)
                .slice(0, 3)
        },
        priorityIcon() {
            const icons = {
                1: 'fal fa-eye',
                2: 'fal fa-check',
                3: 'fal fa-exclamation-triangle',
                4: 'fal fa-fire'
            }
            return icons[this.post.priority] || 'fal fa-circle'
        }
    },
    methods: {
        openLinkPost() {
            this.$router.push(`/posts/${this.post.id}`)
        },
        getInitials(name) {
            if (!name) return '?'
            return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
        }
    }
}
</script>

<style scoped>
.kc-card {
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transition: all 0.2s ease;
}

.kc-card:hover {
    border-color: #0891B2;
    box-shadow: 0 6px 20px rgba(8, 145, 178, 0.12);
    transform: translateY(-2px);
}

.kc-card-img {
    height: 100px;
    overflow: hidden;
}

.kc-card-img img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.kc-card-body {
    padding: 10px 12px;
}

.kc-card-header {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 6px;
}

.kc-card-ref {
    font-size: 0.72rem;
    font-weight: 700;
    color: #94a3b8;
    flex-shrink: 0;
}

.kc-card-title {
    font-size: 0.82rem;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.kc-card-status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    background: #f1f5f9;
    border-radius: 6px;
    font-size: 0.7rem;
    color: #64748b;
    margin-bottom: 8px;
}

.kc-card-status i {
    font-size: 0.68rem;
}

.kc-card-answers {
    list-style: none;
    margin: 0 0 8px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.kc-card-answer {
    display: flex;
    gap: 4px;
    align-items: baseline;
    font-size: 0.72rem;
    line-height: 1.4;
    overflow: hidden;
}

.kc-answer-label {
    flex-shrink: 0;
    color: #94a3b8;
    font-weight: 600;
    white-space: nowrap;
}

.kc-answer-label::after {
    content: ' :';
}

.kc-answer-value {
    color: #334155;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.kc-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 6px;
    border-top: 1px solid #f1f5f9;
}

.kc-card-date {
    font-size: 0.7rem;
    color: #94a3b8;
    display: flex;
    align-items: center;
    gap: 4px;
}

.kc-card-date i {
    font-size: 0.68rem;
}

.kc-card-meta {
    display: flex;
    align-items: center;
    gap: 6px;
}

.kc-card-priority {
    font-size: 0.78rem;
}

.kc-priority--1 { color: #3b82f6; }
.kc-priority--2 { color: #10b981; }
.kc-priority--3 { color: #f59e0b; }
.kc-priority--4 { color: #ef4444; }

.kc-card-author {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: rgba(8, 145, 178, 0.1);
    color: #0891B2;
    font-size: 0.6rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
